import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import './calendar-date-field.css';

const key = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const parse = value => value ? new Date(`${value}T12:00:00`) : new Date();

export default function CalendarDateField({ label, placeholder, value, onChange, min, max }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => parse(value));
  const root = useRef(null);
  const trigger = useRef(null);
  const popup = useRef(null);
  const [position, setPosition] = useState({});
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = trigger.current.getBoundingClientRect();
      const width = Math.min(300, window.innerWidth - 32);
      const height = popup.current?.offsetHeight || 330;
      setPosition({ left: Math.max(16, Math.min(rect.right - width, window.innerWidth - width - 16)),
        top: rect.bottom + height + 4 <= window.innerHeight ? rect.bottom + 4 : Math.max(8, rect.top - height - 4),
        fontFamily: getComputedStyle(trigger.current).fontFamily });
    };
    place();
    window.addEventListener('resize', place);
    document.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); document.removeEventListener('scroll', place, true); };
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    function outside(event) { if (!root.current?.contains(event.target) && !popup.current?.contains(event.target)) setOpen(false); }
    function escape(event) { if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); } }
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [open]);
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const days = Array.from({ length: 42 }, (_, index) => new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay() + index));
  return <div className="calendar-date-field" ref={root}>
    <button ref={trigger} type="button" className="calendar-date-trigger" aria-label={label} aria-haspopup="dialog" aria-expanded={open}
      onClick={() => { setMonth(parse(value || min)); setOpen(current => !current); }}><span>{value ? value.split('-').reverse().join('-') : placeholder}</span><CalendarDays size={14} aria-hidden="true" /></button>
    {open && createPortal(<div ref={popup} style={position} className="calendar-date-field calendar-date-popover" role="dialog" aria-label={`${label} calendar`}>
      <div className="calendar-date-heading">
        <button type="button" aria-label="Previous month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}><ChevronLeft size={16} /></button>
        <strong>{month.toLocaleString('en-US', { month: 'long' })}<span>{month.getFullYear()}</span></strong>
        <button type="button" aria-label="Next month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}><ChevronRight size={16} /></button>
      </div>
      <div className="calendar-date-grid">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => <span className="calendar-date-weekday" key={day}>{day}</span>)}
        {days.map(date => {
          const dateKey = key(date);
          const outside = date.getMonth() !== month.getMonth();
          return <button key={dateKey} type="button" aria-label={`Select ${dateKey}`} aria-pressed={value === dateKey}
            aria-current={dateKey === key(new Date()) ? 'date' : undefined} disabled={outside || (min && dateKey < min) || (max && dateKey > max)}
            onClick={() => { onChange(dateKey); setOpen(false); trigger.current?.focus(); }}>{date.getDate()}</button>;
        })}
      </div>
    </div>, document.body)}
  </div>;
}
